import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { listInvoices } from "@/services/invoices.service";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { fmtMoney, fmtDate, statusColor } from "@/lib/format";
import { Plus, ArrowRight, FileText } from "lucide-react";
import { CreateInvoiceModal } from "@/components/invoices/CreateInvoiceModal";
import FromMilestoneModal from "@/components/invoices/FromMilestoneModal";
import { usePolling } from "@/lib/usePolling";

const prettyStatus = (s) => (s === "UnderReview" ? "Under review" : s);

const Invoices = () => {
  const { user } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [fromMilestoneOpen, setFromMilestoneOpen] = useState(false);
  const [tab, setTab] = useState("all");

  const load = async () => {
    try { setInvoices(await listInvoices()); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  usePolling(load);

  const me = user?.user_id;
  const email = user?.email;

  const filtered = useMemo(() => {
    return invoices.filter((inv) => {
      const iAmPayee = inv.payee_user_id === me;
      const iAmPayer = inv.payer_user_id === me || inv.payer_email === email;
      if (tab === "sent" && !iAmPayee) return false;
      if (tab === "received" && !iAmPayer) return false;
      if (tab === "draft" && inv.status !== "Draft") return false;
      if (tab === "paid" && inv.status !== "Paid") return false;
      if (tab === "overdue" && inv.status !== "Overdue") return false;
      return true;
    });
  }, [invoices, tab, me, email]);

  const totals = useMemo(() => {
    const sentByMe = invoices.filter((i) => i.payee_user_id === me);
    const receivedByMe = invoices.filter((i) => i.payer_user_id === me || i.payer_email === email);
    const outstanding = sentByMe.filter((i) => ["Sent", "Viewed", "Overdue"].includes(i.status)).reduce((s, i) => s + (i.amount || 0), 0);
    const owed = receivedByMe.filter((i) => ["Sent", "Viewed", "Overdue"].includes(i.status)).reduce((s, i) => s + (i.amount || 0), 0);
    return { outstanding, owed };
  }, [invoices, me, email]);

  const tabBtn = (k, l, tid) => (
    <button key={k} type="button" onClick={() => setTab(k)} className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors whitespace-nowrap shrink-0 ${tab === k ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`} data-testid={tid}>
      {l}
    </button>
  );

  return (
    <div className="space-y-6" data-testid="invoices-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-semibold">Invoices</div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mt-1">Invoices</h1>
          <p className="mt-2 text-sm text-muted-foreground">Request payment with a one-tap invoice or generate one from a milestone.</p>
        </div>
        {user?.role !== "admin" && (
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => setFromMilestoneOpen(true)} data-testid="create-from-milestone-btn">
              <FileText className="w-4 h-4 mr-1.5" /> Create from milestone
            </Button>
            <Button onClick={() => setCreateOpen(true)} data-testid="new-invoice-btn">
              <Plus className="w-4 h-4 mr-1.5" /> Request payment
            </Button>
          </div>
        )}
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div className="bg-card border border-border rounded-xl p-5">
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Outstanding (sent by you)</div>
          <div className="mt-2 font-display font-bold text-2xl">{fmtMoney(totals.outstanding)}</div>
        </div>
        <div className="bg-card border border-border rounded-xl p-5">
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Owed (sent to you)</div>
          <div className="mt-2 font-display font-bold text-2xl">{fmtMoney(totals.owed)}</div>
        </div>
      </div>

      <div className="inline-flex max-w-full items-center gap-1 bg-secondary/50 border border-border rounded-lg p-1 overflow-x-auto">
        {tabBtn("all", "All", "invoices-tab-all")}
        {tabBtn("sent", "Sent by me", "invoices-tab-sent")}
        {tabBtn("received", "Sent to me", "invoices-tab-received")}
        {tabBtn("draft", "Drafts", "invoices-tab-draft")}
        {tabBtn("paid", "Paid", "invoices-tab-paid")}
        {tabBtn("overdue", "Overdue", "invoices-tab-overdue")}
      </div>

      {loading ? (
        <div className="text-muted-foreground">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="bg-card border border-border rounded-xl p-12 text-center">
          <FileText className="w-8 h-8 mx-auto text-muted-foreground/60" />
          <div className="mt-3 font-medium" data-testid="invoices-empty">No invoices{tab !== "all" ? ` in ${tab}` : ""} yet.</div>
          <p className="mt-1 text-sm text-muted-foreground max-w-sm mx-auto">Send a payment request or generate one from a contract milestone once work is submitted.</p>
          {user?.role !== "admin" && (
            <Button variant="outline" onClick={() => setCreateOpen(true)} className="mt-4" data-testid="empty-new-invoice-btn">
              <Plus className="w-4 h-4 mr-1.5" /> Request payment
            </Button>
          )}
        </div>
      ) : (
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary/40 text-muted-foreground text-xs uppercase tracking-widest">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">Invoice</th>
                <th className="text-left px-4 py-3 font-semibold hidden md:table-cell">Counterparty</th>
                <th className="text-right px-4 py-3 font-semibold">Amount</th>
                <th className="text-left px-4 py-3 font-semibold hidden sm:table-cell">Due</th>
                <th className="text-left px-4 py-3 font-semibold">Status</th>
                <th className="text-right px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filtered.map((inv) => {
                const iAmPayer = inv.payer_user_id === me || inv.payer_email === email;
                const cp = iAmPayer ? inv.payee_name : inv.payer_name;
                return (
                  <tr key={inv.invoice_id} className="hover:bg-secondary/30" data-testid={`invoice-row-${inv.invoice_id}`}>
                    <td className="px-4 py-3">
                      <Link to={`/invoices/${inv.invoice_id}`} className="font-medium hover:text-primary">{inv.title}</Link>
                      <div className="text-xs text-muted-foreground font-mono mt-0.5">{inv.invoice_id}</div>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground hidden md:table-cell">
                      {cp || "—"}
                      <span className={`ml-2 text-[10px] font-bold tracking-[0.15em] ${iAmPayer ? "text-blue-700" : "text-emerald-700"}`}>{iAmPayer ? "PAYING" : "INVOICING"}</span>
                    </td>
                    <td className="px-4 py-3 text-right font-display font-semibold">{fmtMoney(inv.amount, inv.currency)}</td>
                    <td className="px-4 py-3 text-muted-foreground hidden sm:table-cell">{inv.due_date ? fmtDate(inv.due_date) : "—"}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${statusColor(inv.status)}`}>{prettyStatus(inv.status)}</span>
                      {!inv.milestone_id && inv.onchain_status === "Funded" && inv.status !== "Paid" && (
                        <span className="ml-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">awaiting claim</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Link to={`/invoices/${inv.invoice_id}`} className="text-xs font-semibold text-primary hover:underline inline-flex items-center gap-1" data-testid={`view-invoice-${inv.invoice_id}`}>
                        View <ArrowRight className="w-3 h-3" />
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        </div>
      )}

      <CreateInvoiceModal open={createOpen} onClose={() => setCreateOpen(false)} onCreated={load} />
      <FromMilestoneModal open={fromMilestoneOpen} onClose={() => setFromMilestoneOpen(false)} onCreated={load} />
    </div>
  );
};

export default Invoices;
